import { useState } from 'react';

import { Box, IconButton, Link, Typography } from '@mui/material';

import { useGetSavedRouteAlertsQuery } from 'state/emailAlertsSlices';
import { useAppSelector } from 'state/hooks';
import { ProjectFeatures } from 'state/userSlice';
import { selectCurrentProjectInfo } from 'state/workflowSlice';

import FontAwesomeIcon from 'features/common/FontAwesomeIcon';
import StlPopoverWrapper from 'features/common/StlPopoverWrapper/StlPopoverWrapper';
import { NotificationLog } from 'features/notifications/NotificationLog/NotificationLog';
import { useNotificationProject } from 'features/notifications/useNotificationProject';

import styles from './NotificationPanel.module.css';

export default function NotificationPanel() {
  const userProject = useAppSelector(selectCurrentProjectInfo);
  const { navigateTo } = useNotificationProject();
  const [open, setOpen] = useState<boolean>(false);
  const [count, setCount] = useState<number>(0);

  const projectHasAlerts = userProject?.features.includes(
    ProjectFeatures.ALERTS,
  );

  const { data: alerts } = useGetSavedRouteAlertsQuery(
    userProject?.project_slug,
    {
      skip: !userProject?.project_slug || !projectHasAlerts,
    },
  );

  if (!projectHasAlerts) return null;

  return (
    <StlPopoverWrapper
      keepMounted
      transformOrigin={{ horizontal: 'right', vertical: 'top' }}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      open={open}
      setOpen={setOpen}
      startIcon={<NotificationIcon count={count} />}
      buttonClass={styles.btn}
      paperClass={styles.popoverBody}
    >
      <>
        <Box className={styles.paddedSection}>
          <Box className={styles.popoverHead}>
            <Typography variant="body1">Notifications</Typography>
            <Box display="flex">
              <IconButton
                onClick={() => navigateTo.manage()}
                title="Manage"
                size="small"
              >
                <FontAwesomeIcon name="gear" size="0.875rem" />
              </IconButton>
              <IconButton
                onClick={() => setOpen(false)}
                title="Close"
                size="small"
              >
                <FontAwesomeIcon
                  name="xmark"
                  type="sharp-solid"
                  size="0.875rem"
                />
              </IconButton>
            </Box>
          </Box>
          <NotificationLog
            navMode
            onUnresolvedCountChange={(v) => setCount(v)}
          />
        </Box>
        {alerts?.length > 0 && (
          <Link
            className={styles.link}
            component="button"
            onClick={() => navigateTo.log()}
          >
            View All Notifications
          </Link>
        )}
      </>
    </StlPopoverWrapper>
  );
}

const UNRESOLVED_LIMIT = 9; // max single digit

function NotificationIcon({ count }: { count: number }) {
  return (
    <>
      <FontAwesomeIcon title="Notifications" name="bell" size="1.25rem" />
      {count > 0 && (
        <Box className={styles.count}>
          <Typography variant="h4">
            {count}
            {count > UNRESOLVED_LIMIT && '+'}
          </Typography>
        </Box>
      )}
    </>
  );
}
